import type { ComponentPropsOptions, ExtractPublicPropTypes } from "vue";
import { defineComponent } from "vue";
import { p } from "@/utils/props-builder";
import Skeleton from "./Skeleton";

const props = {
  /**
   * Number of integration credentials to show
   * @default 6
   */
  integrationCount: p.number(6),

  /**
   * Show footer with save button
   * @default true
   */
  showFooter: p.boolean(true),
} satisfies ComponentPropsOptions;

export type IntegrationsSkeletonProps = ExtractPublicPropTypes<typeof props>;

/**
 * IntegrationsSkeleton - Loading placeholder for integrations/credentials page
 *
 * Specialized skeleton for the integrations page with its unique layout:
 * - Card header with description
 * - List of credential inputs with icons, labels, and model badges
 * - Footer with save button
 *
 * @example
 * ```tsx
 * // Basic usage
 * <IntegrationsSkeleton />
 *
 * // Custom integration count
 * <IntegrationsSkeleton integrationCount={8} />
 *
 * // Without footer
 * <IntegrationsSkeleton showFooter={false} />
 *
 * // With QueryLoader
 * <QueryLoader queries={[credentialsQuery, modelsQuery]}>
 *   <QueryLoader.Loading>
 *     <IntegrationsSkeleton />
 *   </QueryLoader.Loading>
 *   <Card>
 *     ...
 *   </Card>
 * </QueryLoader>
 * ```
 */
const IntegrationsSkeleton = defineComponent({
  name: "IntegrationsSkeleton",
  props,
  setup(props) {
    return () => (
      <div class="w-full overflow-hidden rounded-lg border bg-card">
        {/* Header */}
        <div class="space-y-2 p-6 pb-2">
          <Skeleton class="h-3 w-full max-w-2xl rounded" />
          <Skeleton class="h-3 w-3/4 max-w-xl rounded" />
        </div>

        {/* Content - Integration credentials list */}
        <div class="max-h-[calc(100vh-17.6rem)] space-y-6 overflow-auto p-6">
          {Array.from({ length: props.integrationCount }).map((_, i) => (
            <div key={`integration-${i}`} class="max-w-3xl space-y-2">
              {/* Label with icon */}
              <div class="flex items-center gap-1.5">
                <Skeleton class="h-3.5 w-3.5 rounded" />
                <Skeleton class="h-4 w-24 rounded" />
              </div>

              {/* Input field */}
              <Skeleton class="h-10 w-full rounded-md" />

              {/* Helper text with model badges */}
              <div class="flex flex-wrap items-center gap-1.5 px-1">
                <Skeleton class="h-3 w-32 rounded" />
                <Skeleton class="h-5 w-20 rounded-sm" />
                <Skeleton class="h-5 w-24 rounded-sm" />
                <Skeleton class="h-5 w-16 rounded-sm" />
              </div>
            </div>
          ))}
        </div>

        {/* Footer */}
        {props.showFooter && (
          <div class="flex justify-end border-t p-6">
            <Skeleton class="h-10 w-24 rounded-md" />
          </div>
        )}
      </div>
    );
  },
});

export default IntegrationsSkeleton;
